/**
 * WebSocket Client — connects to the AMC WebSocket relay server
 *
 * Follows the remote-agents pattern:
 * - Exponential backoff reconnection (2s → 10s, max 50 attempts)
 * - Keepalive pings every 30s
 * - Pub/sub event model
 * - Token + tenantId passed as query params
 */

import { getAccessToken } from './auth';

// Relative paths (e.g. "/ws/events") are resolved against the current page origin
// so that an nginx reverse-proxy in front of the SPA can forward to the orchestrator.
function resolveWsUrl(raw) {
  if (!raw) return 'ws://localhost:8080/ws/events';
  if (raw.startsWith('/')) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}${raw}`;
  }
  return raw;
}

const WS_URL = resolveWsUrl(window._env_?.VITE_WS_URL || import.meta.env.VITE_WS_URL);

const RECONNECT_BASE = 2000;
const RECONNECT_MAX = 10000;
const MAX_RECONNECT_ATTEMPTS = 50;
const PING_INTERVAL = 30000;

/** @type {Map<string, Set<Function>>} */
const listeners = new Map();

let ws = null;
let reconnectAttempts = 0;
let reconnectTimer = null;
let pingTimer = null;
let tenantId = '';
let sessionId = null;

// ---- Internal helpers ----

function emit(eventType, payload) {
  const fns = listeners.get(eventType);
  if (!fns) return;
  fns.forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      console.error(`[ws] listener error on ${eventType}:`, err);
    }
  });
}

function startPing() {
  stopPing();
  pingTimer = setInterval(() => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'ping' }));
    }
  }, PING_INTERVAL);
}

function stopPing() {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function scheduleReconnect() {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.warn('[ws] Max reconnect attempts reached');
    emit('connection', { status: 'failed' });
    return;
  }
  const delay = Math.min(RECONNECT_BASE * 2 ** reconnectAttempts, RECONNECT_MAX);
  reconnectAttempts++;
  console.log(`[ws] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
  reconnectTimer = setTimeout(() => connect(), delay);
}

// ---- Public API ----

/**
 * Connect to the WebSocket server.
 * @param {string} [userTenantId] — defaults to the Entra ID oid claim
 */
export async function connect(userTenantId) {
  if (userTenantId) tenantId = userTenantId;

  // Cancel any pending reconnect so we don't get overlapping attempts
  clearTimeout(reconnectTimer);
  reconnectTimer = null;

  // Get fresh token
  const token = await getAccessToken();
  if (!token && (window._env_?.VITE_AZURE_CLIENT_ID || import.meta.env.VITE_AZURE_CLIENT_ID)) {
    console.warn('[ws] No access token — skipping connect');
    return;
  }

  // Close existing connection
  if (ws) {
    ws.onclose = null; // prevent reconnect loop
    ws.close();
  }

  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (tenantId) params.set('tenantId', tenantId);

  const url = `${WS_URL}?${params}`;
  ws = new WebSocket(url);

  ws.onopen = () => {
    console.log('[ws] Connected');
    reconnectAttempts = 0;
    startPing();
    emit('connection', { status: 'connected' });
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);

      // Track server session ID for restart detection
      if (data.type === 'session_started') {
        const prevSession = sessionId;
        sessionId = data.sessionId;
        if (prevSession && prevSession !== sessionId) {
          emit('session_restart', { previousSessionId: prevSession, newSessionId: sessionId });
        }
        // Forward to subscribers (realtime.js bridge needs this)
        emit(data.type, data);
        emit('*', data);
        return;
      }

      if (data.type === 'pong') return;

      // Emit by event type
      emit(data.type, data);
      // Also emit a catch-all
      emit('*', data);
    } catch {
      // non-JSON message, ignore
    }
  };

  ws.onclose = (event) => {
    console.log(`[ws] Closed (code=${event.code})`);
    stopPing();
    emit('connection', { status: 'disconnected', code: event.code });
    if (event.code !== 4001) {
      // Don't reconnect on auth failure
      scheduleReconnect();
    }
  };

  ws.onerror = (err) => {
    console.error('[ws] Error:', err);
    emit('connection', { status: 'error' });
  };
}

/**
 * Disconnect and stop reconnection.
 */
export function disconnect() {
  reconnectAttempts = MAX_RECONNECT_ATTEMPTS; // prevent reconnect
  clearTimeout(reconnectTimer);
  stopPing();
  if (ws) {
    ws.onclose = null;
    ws.close();
    ws = null;
  }
}

/**
 * Subscribe to a WebSocket event type.
 * @param {string} eventType — e.g. 'task_updated', 'message_chunk', 'connection', '*'
 * @param {Function} callback
 */
export function subscribe(eventType, callback) {
  if (!listeners.has(eventType)) {
    listeners.set(eventType, new Set());
  }
  listeners.get(eventType).add(callback);
}

/**
 * Unsubscribe from a WebSocket event type.
 */
export function unsubscribe(eventType, callback) {
  const fns = listeners.get(eventType);
  if (fns) {
    fns.delete(callback);
    if (fns.size === 0) listeners.delete(eventType);
  }
}

/**
 * Check if currently connected.
 */
export function isConnected() {
  return ws?.readyState === WebSocket.OPEN;
}

/**
 * The Flight Director session ID (set on session_started).
 * Changes when FD restarts.
 * @returns {string|null}
 */
export function getSessionId() {
  return sessionId;
}
